import React from "react";
import { FaUserCircle } from "react-icons/fa";
import clsx from "clsx";
import config from "@/config";

interface SignUpContentProps {
    onGoogle: () => void;
    onEmail: () => void;
    onDismiss?: () => void;
    showTerms?: boolean;
    variant?: "light" | "dark";
}

const SignUpContent: React.FC<SignUpContentProps> = ({
    onGoogle,
    onEmail,
    onDismiss,
    showTerms = true,
    variant = "light",
}) => {
    const isDark = variant === "dark";
    return (
        <div className="tw-w-full">
            <div className="tw-flex tw-items-center tw-gap-2 tw-mb-1">
                <div className="tw-relative">
                    <div className="tw-w-10 tw-h-10 tw-flex tw-items-center tw-justify-center tw-rounded-full">
                        <FaUserCircle
                            className={clsx(
                                "tw-w-6 tw-h-6 tw-rounded-full",
                                isDark ? "tw-text-white" : "tw-text-gray-900",
                            )}
                        />
                    </div>
                    <div className="tw-absolute tw-top-2 tw-right-1.5 tw-w-2 tw-h-2 tw-bg-red-500 tw-rounded-full tw-animate-pulse "></div>
                </div>
                <div className="tw-flex-1">
                    <p className={`tw-text-sm tw-m-0 ${isDark ? "tw-text-gray-100" : "tw-text-gray-600"}`}>
                        You’re browsing as a guest
                    </p>
                </div>
                {onDismiss && (
                    <button
                        onClick={onDismiss}
                        className="tw-text-gray-400 hover:tw-text-gray-600 tw-transition-colors"
                        aria-label="Dismiss"
                    >
                        <svg className="tw-w-5 tw-h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M6 18L18 6M6 6l12 12"
                            />
                        </svg>
                    </button>
                )}
            </div>

            <h3
                className={`tw-ml-2 tw-font-bold tw-text-sm tw-leading-tight tw-mb-1 ${
                    isDark ? "tw-text-white" : "tw-text-gray-900"
                }`}
            >
                Save your scores and sync across devices
            </h3>
            <p className="tw-text-sm tw-mb-2 tw-ml-2 tw-text-gray-600">Create a free PriceMe account in one tap.</p>

            <div className="tw-flex tw-flex-col tw-gap-3">
                <button
                    onClick={onGoogle}
                    className="tw-w-full tw-bg-white tw-border tw-border-gray-200 tw-text-gray-900 tw-font-semibold tw-py-3 tw-px-6 tw-rounded-[18px] tw-transition tw-shadow-md hover:tw-shadow-lg tw-flex tw-items-center tw-justify-center tw-gap-3"
                >
                    <svg className="tw-w-6 tw-h-6" viewBox="0 0 24 24" fill="currentColor">
                        <path
                            d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                            fill="#4285F4"
                        />
                        <path
                            d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                            fill="#34A853"
                        />
                        <path
                            d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                            fill="#FBBC05"
                        />
                        <path
                            d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                            fill="#EA4335"
                        />
                    </svg>
                    Continue with Google
                </button>

                <button
                    onClick={onEmail}
                    className="tw-w-full tw-bg-[#1f1f1f] tw-text-white tw-font-semibold tw-py-3 tw-px-6 tw-rounded-[18px] tw-transition hover:tw-opacity-90"
                >
                    Continue with email
                </button>
            </div>

            {showTerms && (
                <p
                    className={`tw-text-[0.6em] tw-mt-4 tw-mb-0 tw-text-center ${
                        isDark ? "tw-text-gray-400" : "tw-text-gray-500"
                    }`}
                >
                    By continuing, you agree to PriceMe’s{" "}
                    <a
                        className="tw-underline"
                        href={`${config.BASE_URL}/terms-of-service`}
                        target="_blank"
                        rel="noreferrer"
                    >
                        Terms
                    </a>{" "}
                    and{" "}
                    <a
                        className="tw-underline"
                        href={`${config.BASE_URL}/privacy-policy`}
                        target="_blank"
                        rel="noreferrer"
                    >
                        Privacy Policy
                    </a>
                    .
                </p>
            )}
        </div>
    );
};

export default SignUpContent;
